import { lazy, Suspense, useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { Outlet, useLocation, useNavigate, useNavigationType, useParams, Navigate } from 'react-router-dom';
import { useHomes, useModel, useReady } from '../lib/hooks.js';
import { lastHome } from '../lib/prefs.js';
import { useTrackHistory } from '../lib/nav-history.js';
import { HomeSwitcher } from './HomeSwitcher.js';
import { ConnectionBanner } from './ConnectionBanner.js';
import { useSwitchHome } from './useSwitchHome.js';
import { Sidebar } from './Sidebar.js';
import { NavDrawer } from './NavDrawer.js';
import { EdgeSwipe } from './EdgeSwipe.js';
import { AlarmSheetHost } from './Alarm.js';
import { DoorHost } from '../screens/door/DoorHost.js';
import { SkeletonTiles } from '../components/ui/index.js';
import { ThemeController } from './ThemeController.js';
import { Backdrop } from '../components/Backdrop.js';
import { NowPlayingBar } from './NowPlayingBar.js';
import { onNativeEvent } from '../lib/native.js';
import { useToiletBridge } from '../screens/toilets/data.js';
import { toast } from '../lib/toast.js';

const HistoryHost = lazy(() => import('../components/history/HistorySheet.js'));

/** The app backdrop (also used by the auth screens). */
export const Ambient = Backdrop;

/** App frame: ambient backdrop, desktop sidebar (drawer below 1024px), scrolling content, global sheets, edge swipe. */
export function Shell(): ReactNode {
  const homes = useHomes();
  const switchHome = useSwitchHome();
  const app = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  useTrackHistory();
  useScrollReset(scroller);
  useForegroundPush();
  useNativeNavigate();

  // ⌘1/⌘2 (or Alt+1) switches homes on desktop.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey || e.altKey) || e.shiftKey) return;
      const n = Number(e.key);
      if (!Number.isInteger(n) || n < 1 || n > 9) return;
      const h = homes[n - 1];
      if (!h || homes.length < 2) return;
      e.preventDefault();
      switchHome(h.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [homes, switchHome]);

  return (
    <div className="app" ref={app}>
      <Ambient />
      <Sidebar />
      <main className="app-main">
        <div className="app-scroll" id="scroller" ref={scroller}>
          <Suspense fallback={<div className="shell"><SkeletonTiles /></div>}>
            <Outlet />
          </Suspense>
        </div>
        <NowPlayingBar />
      </main>
      <ThemeController />
      <ConnectionBanner />
      <HomeSwitcher />
      <NavDrawer />
      <AlarmSheetHost />
      <DoorHost />
      <EdgeSwipe appRef={app} />
      <Suspense fallback={null}>
        <HistoryHost />
      </Suspense>
    </div>
  );
}

/** Validates :home, remembers it as the last home, and waits for its snapshot. */
export function HomeLayout(): ReactNode {
  const { home = '' } = useParams();
  const homes = useHomes();
  const ready = useReady();
  const model = useModel(home);
  const known = homes.some((h) => h.id === home);
  useEffect(() => {
    if (known) lastHome.set(home);
  }, [home, known]);
  // iOS app: the open home's shower toilets over Bluetooth (screens/toilets/data.ts); a no-op in a browser.
  useToiletBridge(known ? home : '');
  if (ready && !known) return <Navigate to="/" replace />;
  if (!model)
    return (
      <div className="shell">
        <div className="appbar-skeleton" />
        <SkeletonTiles count={8} />
      </div>
    );
  return <Outlet />;
}

/**
 * The scroller outlives pages, so a new page would open at the old one's offset. Opening a page starts
 * at the top; Back/Forward returns to where that page was left. Query-only changes (?space=) don't scroll.
 */
function useScrollReset(ref: RefObject<HTMLDivElement | null>): void {
  const { pathname, key } = useLocation();
  const type = useNavigationType();
  const saved = useRef(new Map<string, number>());
  const current = useRef(key);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onScroll = (): void => void saved.current.set(current.current, el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [ref]);
  useLayoutEffect(() => {
    current.current = key;
  }, [key]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = type === 'POP' ? (saved.current.get(key) ?? 0) : 0;
    // Pathname changes only.
  }, [pathname]);
}

/** iOS app open: a notification shows as a toast (the app suppresses the system banner), with Open if it links somewhere. */
function useForegroundPush(): void {
  const navigate = useNavigate();
  useEffect(() => onNativeEvent((e) => {
    if (e.type !== 'push.received') return;
    const text = [e.title, e.body].filter(Boolean).join(': ') || 'Notification';
    const path = e.url ? appPath(e.url) : null;
    toast(text, {
      tone: e.level === 'critical' ? 'alert' : e.level === 'time-sensitive' ? 'caution' : 'info',
      ttl: e.level === 'active' ? 6000 : 10000,
      ...(path ? { action: { label: 'Open', run: () => navigate(path) } } : {}),
    });
  }), [navigate]);
}

/** Same-origin path for a notification url ("/h/woodlands/…" or "https://home.eguchi.co.uk/…"), else null. */
function appPath(url: string): string | null {
  try {
    const u = new URL(url, location.origin);
    return u.origin === location.origin ? u.pathname + u.search + u.hash : null;
  } catch {
    return null;
  }
}

/**
 * The iOS shell's deep links (widgets, Live Activities, notifications) navigate in-app through this instead
 * of reloading the page: a reload during a Lock Screen launch can lay the page out wider than the screen.
 */
function useNativeNavigate(): void {
  const navigate = useNavigate();
  useEffect(() => {
    const w = window as unknown as { __ehNavigate?: (path: string) => boolean };
    w.__ehNavigate = (path: string): boolean => {
      if (!path.startsWith('/') || path.startsWith('//')) return false;
      navigate(path);
      return true;
    };
    return () => {
      delete w.__ehNavigate;
    };
  }, [navigate]);
}

